import React, { FC, useCallback, useContext, useMemo, useState } from 'react';

import * as Yup from 'yup';
import { AxiosResponse } from 'axios';

import TextInput from '../../../components/inputs/textInput';
import AampeItalicText from '../../../components/shared/AampeText/AampeItalicText';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';
import {
    BRAZE_EMAIL_VALIDATION,
    SFMC_EMAIL_VALIDATION,
} from '../../../constants/email.validation';

import { EditorContext } from '../../../context/EditorContext';

import useGetCustomerPushIntegration from '../../../hooks/pushIntegrations/useGetCustomerPushIntegration';

import { FormulaCreateModel } from '../../../models/formula.model';
import { PushProviders } from '../../../models/pushProvider.model';

interface FormulaInputTextProps {
    createOrUpdateFormula: (
        updates: FormulaCreateModel,
        onSuccess?: ((value?: number) => void) | undefined,
    ) => Promise<AxiosResponse<any, any>> | null | undefined | void;
    isDisabled?: boolean;
}

const emailSchema = Yup.object({
    email: Yup.string().email('Invalid email address'),
});

const brazeEmailSchema = Yup.object({
    email: Yup.string().matches(
        BRAZE_EMAIL_VALIDATION,
        'Email must match braze "from" email format (Display Name <email@address.com>)',
    ),
});

const sfmcEmailSchema = Yup.object({
    email: Yup.string().matches(
        SFMC_EMAIL_VALIDATION,
        'Email must be a valid address or mailbox format (Display Name <email@address.com>)',
    ),
});

const FormulaFromInput: FC<React.PropsWithChildren<FormulaInputTextProps>> = ({
    createOrUpdateFormula,
    isDisabled,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const [value, setValue] = useState<string | undefined>(
        formula?.additional_metadata?.from_,
    );

    React.useEffect(() => {
        setValue(formula?.additional_metadata?.from_);
    }, [formula?.additional_metadata?.from_]);

    const { providerName } = useGetCustomerPushIntegration();

    const isBraze = providerName === PushProviders.Braze;
    const isSfmc = providerName === PushProviders.Salesforce;
    const isCleverTap = providerName === PushProviders.CleverTap;
    const isCustomerIo = providerName === PushProviders.CustomerIO;

    const [error, setError] = useState<string | undefined>(undefined);
    const [isTouched, setIsTouched] = useState<boolean>(false);

    // CleverTap uses already configured sender_name
    // CustomerIO can use custom user attribute that would be resolved by CustomerIO itself
    const inputValidationRequired: boolean = !(isCleverTap || isCustomerIo);

    const handleUpdate = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value);
            setIsTouched(true);

            if (inputValidationRequired) {
                const schema = isBraze
                    ? brazeEmailSchema
                    : isSfmc
                    ? sfmcEmailSchema
                    : emailSchema;
                schema
                    .validate({ email: e.target.value })
                    .then(() => {
                        setError(undefined);
                        createOrUpdateFormula({
                            additional_metadata: {
                                ...(formula?.additional_metadata ?? {}),
                                from_: e.target.value,
                            },
                        });
                    })
                    .catch((error) => {
                        setError(error.message);
                    });
            } else {
                createOrUpdateFormula({
                    additional_metadata: {
                        ...(formula?.additional_metadata ?? {}),
                        from_: e.target.value,
                    },
                });
                setError(undefined);
            }
        },
        [
            createOrUpdateFormula,
            formula,
            inputValidationRequired,
            isBraze,
            isSfmc,
        ],
    );

    const title = useMemo<string>(() => {
        if (isCleverTap) {
            return 'Sender Name';
        }
        return 'From';
    }, [isCleverTap]);

    const description = useMemo<string>(() => {
        if (isCleverTap) {
            return 'Specify the sender name';
        }
        return 'Specify the senders email address to display to recipients';
    }, [isCleverTap]);

    return (
        <TitleContainer>
            <TitleWithInfo>
                <TitleName>
                    {title} <AampeItalicText text={'(Required)'} />
                </TitleName>
            </TitleWithInfo>

            <StyledTextField
                placeholder={description}
                value={value}
                wrapperStyle={{ padding: aampeTheme.spacing(0) }}
                onChange={handleUpdate}
                name="email"
                error={error}
                touched={isTouched}
                isDisabled={isDisabled}
            />
        </TitleContainer>
    );
};

export default FormulaFromInput;

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: aampeTheme.spacing(1),
});
const TitleName = styled.span({
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
    color: aampeTheme.colors.gray800,
});

const TitleWithInfo = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
});

const StyledTextField = styled(TextInput)({
    width: '100%',
    padding: aampeTheme.spacing(0),
    '& .MuiInputBase-root': {
        backgroundColor: aampeTheme.colors.white,
        borderRadius: aampeTheme.borderRadius.default,
        borderColor: aampeTheme.colors.bordersWG01,
        minHeight: '38px',
        '&:hover fieldset': {
            borderColor: aampeTheme.colors.solidA01,
        },
    },
});
